// Per-scope navigation configuration.
// Routes not in NAV_BY_SCOPE for the current scope remain accessible via
// direct URL but are hidden from the sidebar. Phase 3 will add proper
// route guards.

import {
  LayoutDashboard,
  FileText,
  Building2,
  Receipt,
  ArrowLeftRight,
  PiggyBank,
  Calculator,
  Wallet,
  Settings,
  Bell,
  BarChart3,
  LineChart,
  Plug,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Activity,
  ClipboardList,
  Banknote,
  Send,
  Eye,
  Settings2,
  CreditCard,
  Truck,
  Landmark,
  Gauge,
  Users,
} from 'lucide-react';

export const NAV_BY_SCOPE = {
  // Overall: the group-wide dashboard. The System section (Attention,
  // Diagnostics, Theme, etc.) is not listed per-scope; it lives in
  // SYSTEM_NAV below and renders globally, so Overall only declares its
  // Dashboard entry here, matching every other scope's Overview block.
  overall: [
    { path: '/overall', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
    // Group scope = business entities + personal, on one sheet/series.
    { path: '/overall/assets-liabilities', label: 'Assets & Liabilities', icon: Wallet, group: 'Finance' },
    { path: '/overall/profit-loss', label: 'Profit & Loss', icon: LineChart, group: 'Finance' },
    // Cashflow + Reporting (Fan, 2026-10-22): why a month's cash went down,
    // and the monthly financials PDF download.
    { path: '/overall/cashflow', label: 'Cashflow', icon: ArrowLeftRight, group: 'Finance' },
    // Taxes (Fan, 2026-10-27): group-wide reserves, tax debts and tax paid.
    { path: '/overall/tax', label: 'Taxes', icon: Calculator, group: 'Finance' },
    { path: '/overall/reporting', label: 'Reporting', icon: BarChart3, group: 'Reports' },
  ],
  portfolio: [
    { path: '/portfolio', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
    { path: '/revenue', label: 'Revenue', icon: TrendingUp, group: 'Finance' },
    { path: '/costs', label: 'Costs', icon: Banknote, group: 'Finance' },
    { path: '/profit-loss', label: 'Profit & Loss', icon: LineChart, group: 'Finance' },
    { path: '/invoicing', label: 'Invoicing', icon: Send, group: 'Operations' },
    { path: '/management-statements', label: 'Mgmt Statements', icon: ClipboardList, group: 'Operations' },
    { path: '/reporting', label: 'Reporting', icon: BarChart3, group: 'Reports' },
  ],
  br_os: [
    { path: '/bros-dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
    { path: '/bros/revenue', label: 'Revenue', icon: TrendingUp, group: 'Finance' },
    { path: '/bros/costs', label: 'Costs', icon: Banknote, group: 'Finance' },
    { path: '/bros/profit-loss', label: 'Profit & Loss', icon: LineChart, group: 'Finance' },
    { path: '/bros/tax-reserves', label: 'Tax Reserves', icon: Calculator, group: 'Operations' },
    // Payroll lives in the Entities scope only (Fan, 2026-10-27).
  ],
  personal: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
    { path: '/personal/net-worth', label: 'Assets & Liabilities', icon: Wallet, group: 'Finance' },
    { path: '/personal/profit-loss', label: 'Profit & Loss', icon: LineChart, group: 'Finance' },
    { path: '/personal/banking/balances', label: 'Banking', icon: Landmark, group: 'Operations', matchPrefix: '/personal/banking' },
    { path: '/personal/spendings', label: 'Spendings', icon: CreditCard, group: 'Operations' },
    { path: '/personal/subscriptions', label: 'Fixed costs', icon: RefreshCw, group: 'Operations' },
    { path: '/personal/intercompany', label: 'Intercompany', icon: PiggyBank, group: 'Operations' },
    // Taxes (Fan, 2026-10-29): the Overall Taxes page for the personal entities only.
    { path: '/personal/taxes', label: 'Taxes', icon: Calculator, group: 'Operations' },
  ],
  entities: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
    { path: '/assets-liabilities', label: 'Assets & Liabilities', icon: Wallet, group: 'Finance' },
    { path: '/profit-loss', label: 'Profit & Loss', icon: LineChart, group: 'Finance' },
    { path: '/banking/balances', label: 'Banking', icon: Landmark, group: 'Operations', matchPrefix: '/banking' },
    { path: '/invoices', label: 'Invoices', icon: FileText, group: 'Operations', matchPrefix: '/invoices' },
    // Taxes (Fan, 2026-10-29): the Overall Taxes page for the companies only.
    // The old Tax Reserves page stays at /tax (reserve settings), linked from it.
    { path: '/taxes', label: 'Taxes', icon: Calculator, group: 'Operations' },
    { path: '/payroll', label: 'Payroll', icon: Users, group: 'Operations' },
    { path: '/intercompany', label: 'Intercompany', icon: PiggyBank, group: 'Operations' },
    { path: '/subscriptions', label: 'Fixed costs', icon: RefreshCw, group: 'Operations' },
    { path: '/reporting', label: 'Reporting', icon: BarChart3, group: 'Reports' },
  ],
};

// System items always visible regardless of scope
export const SYSTEM_NAV = [
  { path: '/control', label: 'Control', icon: Gauge },
  { path: '/attention', label: 'Attention', icon: Eye },
  { path: '/entities-admin', label: 'Entities', icon: Building2 },
  { path: '/integrations', label: 'Integrations', icon: Plug },
  { path: '/fx-rates', label: 'FX Rates', icon: DollarSign },
  { path: '/system-health', label: 'System Health', icon: Activity },
  // Diagnostics + Pipeline Diagnostic hidden from the sidebar (Fan, 2026-10-22);
  // both pages still open by direct URL (/diagnostics, /admin/uplisting-diagnostic).
  { path: '/alerts', label: 'Alerts', icon: Bell },
  { path: '/settings', label: 'Settings', icon: Settings },
];